import { useCallback, useMemo } from "react";
import { useAuth } from "../configs/plugins/CookieAuthPlugin";

// Permission types with all possible case variations
const PERMISSION_TYPES = {
  VIEW: ["VIEW", "View", "view"],
  ADD: ["ADD", "Add", "add"],
  EDIT: ["EDIT", "Edit", "edit"],
  DELETE: ["DELETE", "Delete", "delete"],
  RESET: ["RESET", "Reset", "reset"],
};

// Utility to normalize permission types
const normalizePermissionType = (type) => {
  if (typeof type !== "string") {
    console.warn(`Invalid permission type: ${type}. Expected a string.`);
    return "";
  }
  const upperType = type.toUpperCase();
  return Object.keys(PERMISSION_TYPES).find((key) => PERMISSION_TYPES[key].includes(type)) || upperType;
};

/**
 * Custom hook to manage user permissions for a specific menu
 * @param {string} menuId - The unique identifier for the menu
 * @returns {Object} Permission checking utilities and status
 */
export default function usePermissions(menuId) {
  const { menuPermission } = useAuth()?.user || {};

  // Validate menuId
  if (!menuId || typeof menuId !== "string") {
    console.warn(`Invalid menuId: ${menuId}. Expected a non-empty string.`);
  }

  // Memoized permission checker
  const checkPermission = useCallback(
    (permissionType) => {
      try {
        if (!menuPermission || !menuId) return false;

        const permissions = menuPermission[menuId];
        if (!permissions || typeof permissions !== "object") return false;

        const normalizedType = normalizePermissionType(permissionType);
        if (!normalizedType) return false;

        const variants = PERMISSION_TYPES[normalizedType];
        return variants?.some((variant) => Boolean(permissions[variant])) || false;
      } catch (error) {
        console.error(`Error checking permission ${permissionType}:`, error);
        return false;
      }
    },
    [menuId, menuPermission],
  );

  // Memoized permission key finder
  const findExistingPermissionKey = useCallback(
    (permissionType) => {
      try {
        if (!menuPermission?.[menuId]) return null;

        const normalizedType = normalizePermissionType(permissionType);
        return PERMISSION_TYPES[normalizedType]?.find((variant) => menuPermission[menuId][variant] !== undefined) || null;
      } catch (error) {
        console.error(`Error finding permission key ${permissionType}:`, error);
        return null;
      }
    },
    [menuId, menuPermission],
  );

  // Memoized permissions object
  const permissions = useMemo(() => {
    const basePermissions = {
      isAllowView: checkPermission("VIEW"),
      isAllowAdd: checkPermission("ADD"),
      isAllowEdit: checkPermission("EDIT"),
      isAllowDelete: checkPermission("DELETE"),
      isAllowReset: checkPermission("RESET"),
    };

    return {
      ...basePermissions,

      // Check if user has any permissions at all
      hasAnyPermissions: Object.values(basePermissions).some(Boolean),

      // Utility methods
      hasPermission: (type) => checkPermission(type),
      hasAnyPermission: (...types) => types.some((type) => checkPermission(type)),
      hasAllPermissions: (...types) => types.every((type) => checkPermission(type)),
      getPermissionKey: (type) => findExistingPermissionKey(type),
      getAllPermissions: () => (menuPermission?.[menuId] ? Object.keys(menuPermission[menuId]) : []),
    };
  }, [checkPermission, findExistingPermissionKey, menuId, menuPermission]);

  return permissions;
}
